import React, { forwardRef, useEffect, useState } from 'react';
import VBuck from './VBuck';
import {
  Ticket, Trophy, Sparkles, ArrowRight, Handshake, CheckCircle2, Clock,
} from 'lucide-react';
import {
  nextDrawDate, timeLeft, countCompleted, totalPoolEntries,
  PRIZE_TIERS, RECENT_WINNERS, WEEKLY_PRIZE,
} from '../lib/lottery';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const LotterySection = forwardRef(function LotterySection({ trades }, ref) {
  const [now, setNow] = useState(new Date());
  const [remoteSettings, setRemoteSettings] = useState(null);
  const [remoteEntries, setRemoteEntries] = useState(null);
  const { isAuthed } = useAuth();
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.getLotterySettings();
        if (!cancelled) setRemoteSettings(s);
      } catch { /* fall back to local defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // Fetch the signed-in user's real ticket count from the backend so the
  // homepage widget matches the admin leaderboard exactly.
  useEffect(() => {
    if (!isAuthed) { setRemoteEntries(null); return () => {}; }
    let cancelled = false;
    (async () => {
      try {
        const e = await api.getMyLotteryEntries();
        if (!cancelled) setRemoteEntries(e);
      } catch { /* ignore — falls back to local trade count */ }
    })();
    return () => { cancelled = true; };
  }, [isAuthed]);

  // Prefer admin-set draw date + prize when available; else use hard-coded fallback.
  const target = remoteSettings?.draw_at
    ? new Date(remoteSettings.draw_at)
    : nextDrawDate(now);
  const weeklyPrize = remoteSettings?.weekly_prize || WEEKLY_PRIZE;
  const { days, hours, minutes, seconds } = timeLeft(target, now);
  // Real backend entries when signed in, else fall back to the localStorage
  // trade sheet count so guests still see something meaningful.
  const myEntries = remoteEntries?.total_entries ?? countCompleted(trades);
  // Community-wide pool always comes from the backend so guests see the same
  // number as signed-in users and it updates as new tickets are earned.
  const pool = remoteSettings?.pool_total_entries ?? totalPoolEntries(trades);
  const odds = pool > 0 ? ((myEntries / pool) * 100).toFixed(2) : '0.00';

  const formatDraw = target.toLocaleString(undefined, {
    weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  return (
    <section ref={ref} className="max-w-7xl mx-auto px-3 sm:px-6 mt-6">
      <div
        className="relative rounded-3xl overflow-hidden border-[3px] shadow-[0_8px_0_var(--t-card-border)]"
        style={{ borderColor: 'var(--t-card-border)' }}
      >
        {/* Top banner */}
        <div
          className="relative p-6 md:p-8"
          style={{
            background:
              'radial-gradient(circle at 8% 12%, #ffd90099 0%, transparent 38%),' +
              'radial-gradient(circle at 92% 8%, #00b8ff99 0%, transparent 35%),' +
              'radial-gradient(circle at 50% 90%, #ff2bd6cc 0%, transparent 45%),' +
              'linear-gradient(180deg, #3a1064 0%, #1a0440 100%)',
          }}
        >
          {/* sparkle dots */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none opacity-50"
            style={{
              backgroundImage:
                'radial-gradient(circle, #ffd90033 1px, transparent 1.5px), radial-gradient(circle, #00b8ff44 1px, transparent 2px)',
              backgroundSize: '36px 36px, 90px 90px',
              backgroundPosition: '0 0, 18px 18px',
            }}
          />

          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            {/* Left: pitch */}
            <div>
              <div className="inline-flex items-center gap-1.5 bg-[#ffd900] text-[#3a1064] px-3 py-1 rounded-full border-2 border-[#3a1064] shadow-[0_3px_0_#1a0440] text-[11px] font-black uppercase tracking-[2px]">
                <Sparkles className="h-3.5 w-3.5" /> V-Bucks Lottery · Live
              </div>
              <h2 className="mt-3 font-black text-3xl md:text-4xl leading-[1.05] text-[#ffd900]">
                Turn your sprite grind <br />
                into <span className="text-[#ff2bd6]">real V-Bucks.</span>
              </h2>
              <p className="mt-3 text-white/90 font-bold max-w-md">
                Every successful trade you complete on Sprite Vault is one entry into our
                weekly V-Bucks drawing. Stop grinding for nothing — turn those countless hours
                hunting Sprite Dust into real benefits for your Fortnite account.
              </p>

              {/* How it works */}
              <div className="mt-4 grid grid-cols-3 gap-2">
                <Step n={1} icon={<Handshake className="h-4 w-4" />} text="Propose a trade" />
                <Step n={2} icon={<CheckCircle2 className="h-4 w-4" />} text="Mark it Completed" />
                <Step n={3} icon={<Ticket className="h-4 w-4" />} text="Get an entry" />
              </div>
            </div>

            {/* Right: prize card */}
            <div className="bg-white rounded-2xl border-[3px] border-[#ffd900] shadow-[0_6px_0_#ffd900,0_0_30px_rgba(255,214,0,0.4)] p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[3px] text-[#3a1064]/70">
                    This week&apos;s grand prize
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <VBuck size={34} />
                    <span className="text-4xl md:text-5xl font-black text-[#3a1064]">
                      {weeklyPrize.toLocaleString()}
                    </span>
                    <span className="font-black text-[#3a1064] text-base">V-Bucks</span>
                  </div>
                </div>
                <Trophy className="h-10 w-10 text-yellow-500" fill="#ffd900" />
              </div>

              {/* Countdown */}
              <div className="mt-4">
                <div className="flex items-center gap-2 text-[11px] font-black text-[#3a1064]/70 uppercase tracking-wider mb-1.5">
                  <Clock className="h-3.5 w-3.5" /> Next draw · {formatDraw}
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { label: 'DAYS', val: days },
                    { label: 'HRS', val: hours },
                    { label: 'MIN', val: minutes },
                    { label: 'SEC', val: seconds },
                  ].map((u) => (
                    <div
                      key={u.label}
                      className="bg-[#3a1064] rounded-lg border-2 border-[#3a1064] py-2 text-center shadow-[0_2px_0_#1a0440]"
                    >
                      <div className="text-2xl font-black text-yellow-300 leading-none">
                        {String(u.val).padStart(2, '0')}
                      </div>
                      <div className="text-[9px] font-black text-white/70 mt-0.5 tracking-wider">
                        {u.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* User entries */}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="rounded-lg p-2.5 bg-[#ffd900] border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064]">
                  <div className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]">
                    Your entries
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Ticket className="h-4 w-4 text-[#3a1064]" />
                    <span className="text-2xl font-black text-[#3a1064] leading-none">{myEntries}</span>
                  </div>
                </div>
                <div className="rounded-lg p-2.5 bg-[#00b8ff] border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064]">
                  <div className="text-[10px] font-black uppercase tracking-wider text-white">
                    Win odds
                  </div>
                  <div className="flex items-baseline gap-1 mt-0.5">
                    <span className="text-2xl font-black text-white leading-none">{odds}%</span>
                    <span className="text-[10px] font-bold text-white/85">of {pool.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lower row: tiers + recent winners */}
        <div
          className="grid md:grid-cols-2 gap-0"
          style={{ background: 'var(--t-card)' }}
        >
          <div className="p-5 md:p-6 border-r-0 md:border-r-2 border-[#3a1064]/15">
            <div className="text-xs font-black uppercase tracking-[2px] mb-3" style={{ color: 'var(--t-text)' }}>
              Weekly prize tiers
            </div>
            <div className="space-y-2">
              {PRIZE_TIERS.map((p) => (
                <div
                  key={p.rank}
                  className="flex items-center justify-between rounded-xl p-3 border-2"
                  style={{
                    borderColor: 'var(--t-card-border)',
                    background: `linear-gradient(90deg, ${p.color}44, transparent)`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="h-9 w-9 rounded-full border-2 flex items-center justify-center font-black"
                      style={{ background: p.color, borderColor: '#3a1064', color: '#3a1064' }}
                    >
                      {p.rank.charAt(0)}
                    </div>
                    <div className="font-black" style={{ color: 'var(--t-text)' }}>{p.rank}</div>
                  </div>
                  <div className="flex items-center gap-1.5 font-black" style={{ color: 'var(--t-text)' }}>
                    <VBuck size={18} />
                    {p.vbucks.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-5 md:p-6">
            <div className="text-xs font-black uppercase tracking-[2px] mb-3" style={{ color: 'var(--t-text)' }}>
              Recent winners
            </div>
            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
              {RECENT_WINNERS.map((w, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between rounded-xl p-3 border-2"
                  style={{
                    borderColor: 'var(--t-card-border)',
                    background: i === 0
                      ? 'linear-gradient(90deg, #ffd90044, transparent)'
                      : 'transparent',
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#ff2bd6] to-[#9d3dff] border-2 border-[#3a1064] flex items-center justify-center text-white font-black">
                      {w.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="font-black truncate" style={{ color: 'var(--t-text)' }}>{w.name}</div>
                      <div className="text-[11px] font-bold" style={{ color: 'var(--t-text-soft)' }}>{w.week}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 font-black" style={{ color: 'var(--t-text)' }}>
                    <VBuck size={16} />
                    {w.vbucks.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: 'var(--t-text-soft)' }}>
              <ArrowRight className="h-3.5 w-3.5" />
              Complete a trade in your <span className="font-black" style={{ color: 'var(--t-text)' }}>Trades</span> drawer to enter.
            </div>
          </div>
        </div>

        {/* Fine print */}
        <div
          className="px-5 md:px-8 py-3 text-[10px] font-bold border-t-2"
          style={{
            background: 'var(--t-muted-bg)',
            color: 'var(--t-text-soft)',
            borderColor: 'var(--t-card-border)',
          }}
        >
          Drawings held weekly at 8:00 PM local time. V-Bucks delivered as Epic gift cards. Unofficial fan project — not affiliated with Epic Games.
        </div>
      </div>
    </section>
  );
});

function Step({ n, icon, text }) {
  return (
    <div className="bg-white/15 backdrop-blur border-2 border-white/25 rounded-xl px-2.5 py-2">
      <div className="flex items-center gap-1.5 text-yellow-300 text-[10px] font-black uppercase tracking-wider">
        <span className="h-4 w-4 rounded-full bg-yellow-300 text-[#3a1064] flex items-center justify-center text-[10px] font-black">
          {n}
        </span>
        {icon}
      </div>
      <div className="text-white font-black text-xs mt-1 leading-tight">{text}</div>
    </div>
  );
}

export default LotterySection;
